import { useEffect, useState } from "react";
import {
  Alert, Button, Card, Group, Select, Stack, Switch, Tabs, Text,
  TextInput, Textarea, Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@components/PermissionGate";
import { Partner, PartnerText } from "@customTypes/partner";
import { useUpdatePartner } from "@hooks/mutations/partner/useUpdatePartner";

interface PartnerPageEditorProps {
  partner: Partner;
  onSaved: (partner: Partner) => void;
}

export function PartnerPageEditor({ partner, onSaved }: PartnerPageEditorProps) {
  const { t } = useTranslation();
  const [lang, setLang] = useState("en");
  const mutation = useUpdatePartner(partner._id);
  const form = useForm({
    initialValues: {
      whatsapp_number: partner.whatsapp_number ?? "",
      page_content: partner.page_content ?? { benefits: [], faqs: [] },
      service_categories: partner.service_categories ?? [],
    },
  });

  useEffect(() => {
    form.setValues({
      whatsapp_number: partner.whatsapp_number ?? "",
      page_content: partner.page_content ?? { benefits: [], faqs: [] },
      service_categories: partner.service_categories ?? [],
    });
    form.resetDirty();
  }, [partner]);

  const localizedInput = (label: string, path: string, value?: PartnerText, multiline = false) => {
    const Component = multiline ? Textarea : TextInput;
    return (
      <Component
        label={`${label} (${lang.toUpperCase()})`}
        value={typeof value === "string" ? value : value?.[lang as "en" | "fr"] ?? ""}
        maxLength={multiline ? 2000 : 200}
        onChange={event => form.setFieldValue(path, {
          ...(typeof value === "string" ? { en: value } : value),
          [lang]: event.currentTarget.value,
        })}
      />
    );
  };

  const itemActions = (path: string, index: number, length: number) => (
    <Group>
      <Button size="xs" disabled={!index} aria-label={t("partner-page.move-up")}
        onClick={() => form.reorderListItem(path, { from: index, to: index - 1 })}>↑</Button>
      <Button size="xs" disabled={index === length - 1} aria-label={t("partner-page.move-down")}
        onClick={() => form.reorderListItem(path, { from: index, to: index + 1 })}>↓</Button>
      <Button size="xs" color="red" onClick={() => form.removeListItem(path, index)}>
        {t("partner-page.remove")}
      </Button>
    </Group>
  );

  const { page_content: content, service_categories: categories } = form.values;

  return (
    <form onSubmit={form.onSubmit(values => mutation.mutate(values, { onSuccess: onSaved }))}>
      <Stack>
        <Alert>{t("partner-page.content-help")}</Alert>
        <TextInput label={t("partner-page.whatsapp-support-international-number")}
          placeholder="+225…" {...form.getInputProps("whatsapp_number")} />
        <Tabs value={lang} onChange={value => setLang(value ?? "en")}>
          <Tabs.List><Tabs.Tab value="en">English</Tabs.Tab><Tabs.Tab value="fr">Français</Tabs.Tab></Tabs.List>
        </Tabs>
        {localizedInput(t("partner-page.hero-title"), "page_content.hero_title", content.hero_title)}
        {localizedInput(t("partner-page.hero-description"), "page_content.hero_description", content.hero_description, true)}

        <Title order={4}>{t("partner-page.healthcare-benefits")}</Title>
        {content.benefits.map((item, index) => {
          const path = `page_content.benefits.${index}`;
          return (
            <Card withBorder key={index}>
              <Stack gap="xs">
                {localizedInput(t("partner-page.title"), `${path}.title`, item.title)}
                {localizedInput(t("partner-page.description"), `${path}.description`, item.description, true)}
                <Select label={t("partner-page.icon")}
                  data={["doctor", "billing", "users", "globe", "monitor", "support"].map(value => ({
                    value, label: t(`partner-page.icon-${value}`),
                  }))}
                  {...form.getInputProps(`${path}.icon`)} />
                <Switch label={t("partner-page.visible")} {...form.getInputProps(`${path}.is_visible`, { type: "checkbox" })} />
                {itemActions("page_content.benefits", index, content.benefits.length)}
              </Stack>
            </Card>
          );
        })}
        <Button variant="light" disabled={content.benefits.length >= 12}
          onClick={() => form.insertListItem("page_content.benefits", { title: {}, description: {}, icon: "doctor", is_visible: true })}>
          {t("partner-page.add-benefit")}
        </Button>

        <Title order={4}>FAQ</Title>
        {content.faqs.map((item, index) => {
          const path = `page_content.faqs.${index}`;
          return (
            <Card withBorder key={index}>
              <Stack gap="xs">
                {localizedInput(t("partner-page.question"), `${path}.question`, item.question)}
                {localizedInput(t("partner-page.answer"), `${path}.answer`, item.answer, true)}
                <Switch label={t("partner-page.visible")} {...form.getInputProps(`${path}.is_visible`, { type: "checkbox" })} />
                {itemActions("page_content.faqs", index, content.faqs.length)}
              </Stack>
            </Card>
          );
        })}
        <Button variant="light" disabled={content.faqs.length >= 20}
          onClick={() => form.insertListItem("page_content.faqs", { question: {}, answer: {}, is_visible: true })}>
          {t("partner-page.add-faq")}
        </Button>

        <Title order={4}>{t("partner-page.service-card-content")}</Title>
        {categories.map((item, index) => {
          const path = `service_categories.${index}`;
          const translation = item.translations?.[lang] ?? {};
          return (
            <Card withBorder key={item.category}>
              <Stack gap="xs">
                <Text fw={600}>{item.title}</Text>
                <Select clearable label={t("partner-page.card-image-from-branding-gallery")}
                  data={(partner.gallery_images ?? []).map((value, i) => ({ value, label: `${t("partner-page.image")} ${i + 1}` }))}
                  value={item.image ?? null} onChange={value => form.setFieldValue(`${path}.image`, value ?? undefined)} />
                <TextInput label={t("partner-page.title")} placeholder={item.title}
                  value={translation.title ?? ""} onChange={event => form.setFieldValue(`${path}.translations.${lang}.title`, event.currentTarget.value)} />
                <Textarea label={t("partner-page.description")} placeholder={item.description}
                  value={translation.description ?? ""} onChange={event => form.setFieldValue(`${path}.translations.${lang}.description`, event.currentTarget.value)} />
                <TextInput label={t("partner-page.button-label")} placeholder={item.button_label}
                  value={translation.button_label ?? ""} onChange={event => form.setFieldValue(`${path}.translations.${lang}.button_label`, event.currentTarget.value)} />
                <Textarea label={t("partner-page.feature-bullets-one-per-line-maximum-8")}
                  value={(translation.features ?? item.features ?? []).join("\n")}
                  onChange={event => form.setFieldValue(`${path}.translations.${lang}.features`, event.currentTarget.value.split("\n").slice(0, 8))} />
              </Stack>
            </Card>
          );
        })}
        {mutation.isError && <Alert color="red">{t("partner-page.save-error")}</Alert>}
        <PermissionGate permission="partners.edit">
          <Button type="submit" loading={mutation.isLoading}>{t("partner-page.save-page-content")}</Button>
        </PermissionGate>
      </Stack>
    </form>
  );
}
